import * as THREE from 'three';
import type { AssetRegistry } from '@/contracts';

interface Entry { value: unknown; bytes: number }

/** Rough GPU footprint of a texture, accounting for the mip chain. */
function textureBytes(t: THREE.Texture): number {
  const img = t.image as { width?: number; height?: number } | undefined;
  const w = img?.width ?? 0;
  const h = img?.height ?? 0;
  if (!w || !h) return 0;
  const base = w * h * 4;
  return t.generateMipmaps ? Math.ceil(base * 1.34) : base;
}

function measure(v: unknown): number {
  if (v instanceof THREE.Texture) return textureBytes(v);
  if (v instanceof THREE.BufferGeometry) {
    let n = 0;
    for (const a of Object.values(v.attributes)) n += (a as THREE.BufferAttribute).array.byteLength;
    if (v.index) n += v.index.array.byteLength;
    return n;
  }
  if (v && typeof v === 'object') {
    // PbrTextureSet and similar bags of textures.
    let n = 0;
    for (const x of Object.values(v as Record<string, unknown>)) if (x instanceof THREE.Texture) n += textureBytes(x);
    return n;
  }
  return 0;
}

/**
 * Memoising registry for procedurally generated assets.
 *
 * Keys must fully describe the output — including quality tier — or two callers
 * wanting different variants will silently share one.
 */
export class Assets implements AssetRegistry {
  private readonly map = new Map<string, Entry>();
  private bytes = 0;

  cache<T>(key: string, make: () => T): T {
    const hit = this.map.get(key);
    if (hit) return hit.value as T;
    const value = make();
    const b = measure(value);
    this.map.set(key, { value, bytes: b });
    this.bytes += b;
    return value;
  }

  has(key: string): boolean { return this.map.has(key); }

  get estimatedBytes(): number { return this.bytes; }

  dispose(key: string): void {
    const e = this.map.get(key);
    if (!e) return;
    disposeDeep(e.value);
    this.bytes -= e.bytes;
    this.map.delete(key);
  }

  disposeAll(): void {
    for (const k of [...this.map.keys()]) this.dispose(k);
  }

  /** Snapshot for the perf overlay: largest entries first. */
  report(): { key: string; mb: number }[] {
    return [...this.map.entries()]
      .map(([key, e]) => ({ key, mb: e.bytes / 1048576 }))
      .sort((a, b) => b.mb - a.mb);
  }
}

function disposeDeep(v: unknown): void {
  if (!v) return;
  if (v instanceof THREE.Texture || v instanceof THREE.BufferGeometry || v instanceof THREE.Material) {
    (v as { dispose(): void }).dispose();
    return;
  }
  if (typeof v === 'object') for (const x of Object.values(v as Record<string, unknown>)) disposeDeep(x);
}
